import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom'; 
import { signUpWithEmail, signInWithGoogle, completeGoogleSignup } from '../services/userService'; 
import Spinner from '../components/Spinner';
import { useAuth } from '../context/AuthContext'; 
import { getAuth } from 'firebase/auth'; // Add missing import

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation(); // Get location object
  const [googleProfile, setGoogleProfile] = useState(location.state?.googleProfile || null); // Make it state

  const [formData, setFormData] = useState({
    // Pre-fill based on googleProfile if available, otherwise empty strings
    email: googleProfile?.email || '',
    password: '',
    confirmPassword: '',
    displayName: googleProfile?.displayName || '' 
  });
  const [ageConfirmed, setAgeConfirmed] = useState(false); 
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const { checkAuthenticationStatus, isAuthenticated, userInfo, userInfoLoading } = useAuth();

  // Effect to update formData when googleProfile changes
  useEffect(() => {
    if (googleProfile) {
      setFormData(prev => ({
        ...prev,
        email: googleProfile.email || '',
        displayName: googleProfile.displayName || ''
      }));
    }
  }, [googleProfile]);

  // Effect to redirect if already authenticated AND has complete user info
  useEffect(() => {
    // Only redirect if user is authenticated AND has valid backend user info
    // This prevents redirecting during Google signup completion where Firebase auth exists but backend user doesn't
    if (isAuthenticated && userInfo && !userInfoLoading) {
      console.log('SignUp: User is authenticated with complete profile, navigating to /chat');
      navigate('/chat');
    } else if (isAuthenticated && !userInfo && !userInfoLoading) {
      console.log('SignUp: User is authenticated but missing backend profile, staying on signup to complete');
    }
  }, [isAuthenticated, userInfo, userInfoLoading, navigate]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target; 
    if (type === 'checkbox') {
      if (name === 'ageConfirmed') {
        setAgeConfirmed(checked);
      } else if (name === 'termsAccepted') {
        setTermsAccepted(checked);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Handler for standard Email/Password Signup
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match'); return;
    }
    if (formData.password.length < 6) {
      setError('Password should be at least 6 characters'); return;
    }
    if (!ageConfirmed) {
      setError('You must confirm you are 18 years or older to sign up.'); return;
    }
    if (!termsAccepted) {
      setError('You must accept the Terms of Use and Privacy Policy to sign up.'); return;
    }
    
    setIsSubmitting(true);
    try {
      const userCredential = await signUpWithEmail(formData.email, formData.password, formData.displayName, ageConfirmed); 
      console.log('handleEmailSubmit: signUpWithEmail succeeded, calling checkAuthenticationStatus()');
      await checkAuthenticationStatus(userCredential, null, true); // Pass true for isFromSignup 
    } catch (error) {
      console.error("Email sign up error caught in SignUp.js:", error);
      if (error.message.includes('Backend signup failed')) {
         setError('Account creation failed. Could not register with server.');
      } else if (error.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please log in.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Google Signup Completion
  const handleGoogleCompleteSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!ageConfirmed) {
      setError('You must confirm you are 18 years or older to complete sign up.'); return;
    }
    if (!termsAccepted) {
      setError('You must accept the Terms of Use and Privacy Policy to complete sign up.'); return;
    }
    if (!formData.displayName.trim()) {
       setError('Please enter your name.'); return;
    }
    if (!googleProfile || !googleProfile.uid) {
       setError('An error occurred. Google user information is missing.'); return;
    }

    setIsSubmitting(true);
    try {
      await completeGoogleSignup(
        googleProfile.uid,
        googleProfile.email, 
        formData.displayName, 
        ageConfirmed
      );
      console.log('handleGoogleCompleteSubmit: completeGoogleSignup succeeded.');
      
       const currentUser = getAuth().currentUser; 
       if (currentUser && currentUser.uid === googleProfile.uid) {
           // Log the type and value of checkAuthenticationStatus right before calling it
           console.log('handleGoogleCompleteSubmit: typeof checkAuthenticationStatus:', typeof checkAuthenticationStatus);
           console.log('handleGoogleCompleteSubmit: value of checkAuthenticationStatus:', checkAuthenticationStatus);
           
           // Ensure it's actually a function before calling
           if (typeof checkAuthenticationStatus === 'function') {
               await checkAuthenticationStatus({ user: currentUser }, null, true); // Pass true for isFromSignup
           } else {
               // Throw a more specific error if it's not a function
               throw new Error("checkAuthenticationStatus is not available or not a function in AuthContext.");
           }
       } else {
           throw new Error("Could not verify current user after Google signup completion.");
       }

    } catch (error) {
       // Log the specific error caught here
       console.error("Google signup completion error caught in SignUp.js:", error); 
       // Set a user-friendly error message
       setError(error.message || 'Failed to complete Google sign up. Please try again.'); 
    } finally {
       setIsSubmitting(false);
    }
  };
  
  // Handler for the Google Sign-In button *on the SignUp page*
  // This should behave like the one on the Home page
  const handleGoogleSignInClick = async () => {
    setIsSubmitting(true); // Use same loading state
    setError('');
    try {
      const result = await signInWithGoogle(); 
      if (result && result.needsCompletion) {
        // New user needs completion - set the googleProfile state and update form
        console.log('handleGoogleSignInClick: New user needs completion, setting up completion form');
        setGoogleProfile(result.profile); // Set the googleProfile state
        setFormData({
           email: result.profile.email || '',
           displayName: result.profile.displayName || '',
           password: '', confirmPassword: ''
        });
        // No navigation needed, they are already here.
      } else if (result && result.user) {
        console.log('handleGoogleSignInClick: Existing user signed in, calling checkAuthenticationStatus()');
        await checkAuthenticationStatus(result, null, false); // False since this is existing user login 
      } else {
        console.error("Google sign-in returned unexpected result:", result);
        setError('An unexpected error occurred during Google sign-in.');
      }
    } catch (error) {
      console.error("Google sign-in error caught in SignUp.js:", error);
       if (error.message.includes('Backend')) { 
         setError('Sign up/Login failed. Could not verify with server.');
      } else if (error.code === 'auth/popup-closed-by-user') {
         setError('Sign-in cancelled.');
      } else {
         setError('Failed to sign in with Google. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-bela-50 to-white flex items-center">
      <div className="container mx-auto px-4 py-8 max-w-md">
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
          <div className="text-center mb-6">
            {/* Adjust title based on flow */}
            <h1 className="text-2xl font-bold text-gray-900">
              {googleProfile ? 'Complete Your Profile' : 'Join The Bela Experience'}
            </h1>
            <p className="text-gray-600 mt-2">
              {googleProfile ? 'Confirm your details to start chatting' : 'Create your account to start chatting'}
            </p>
          </div>
          
          {/* Use different submit handler based on googleProfile */}
          <form onSubmit={googleProfile ? handleGoogleCompleteSubmit : handleEmailSubmit} className="space-y-4">
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                placeholder="Your name"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-bela-500"
                required
                disabled={isSubmitting} // Only disable based on submission state
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className={`w-full px-4 py-3 rounded-lg border ${googleProfile ? 'border-gray-200 bg-gray-100 text-gray-500' : 'border-gray-300'} focus:outline-none focus:border-bela-500`}
                required
                disabled={isSubmitting || !!googleProfile} // Disable if googleProfile exists OR submitting
              />
            </div>
            
            {/* Conditionally render password fields only if NOT googleProfile flow */}
            {!googleProfile && (
              <>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="At least 6 characters"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-bela-500"
                      required
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-600"
                      tabIndex="-1"
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Re-enter your password"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-bela-500"
                      required
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-600"
                      tabIndex="-1"
                    >
                      {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Age Confirmation Checkbox - Always show */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="ageConfirmation"
                  name="ageConfirmed"
                  type="checkbox"
                  checked={ageConfirmed}
                  onChange={handleChange}
                  className="focus:ring-bela-500 h-4 w-4 text-bela-600 border-gray-300 rounded"
                  required 
                  disabled={isSubmitting}
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="ageConfirmation" className="font-medium text-gray-700">
                  I confirm that I am 18 years of age or older.
                </label>
              </div>
            </div>

            {/* Terms and Privacy Policy Acceptance Checkbox */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="termsAcceptance"
                  name="termsAccepted"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={handleChange}
                  className="focus:ring-bela-500 h-4 w-4 text-bela-600 border-gray-300 rounded"
                  required 
                  disabled={isSubmitting}
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="termsAcceptance" className="font-medium text-gray-700">
                  I agree to the{' '}
                  <a 
                    href="/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-bela-600 hover:text-bela-700 underline"
                  >
                    Terms of Use
                  </a>
                  {' '}and{' '}
                  <a 
                    href="/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-bela-600 hover:text-bela-700 underline"
                  >
                    Privacy Policy
                  </a>
                  .
                </label>
              </div>
            </div>
            
            {error && <p className="text-red-500 text-sm">{error}</p>}
            
            {/* Adjust button text based on flow */}
            <button 
              type="submit"
              className="w-full py-3 bg-bela-600 hover:bg-bela-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <> <Spinner /> <span>{googleProfile ? 'Completing Sign Up...' : 'Creating Account...'}</span> </>
              ) : (
                googleProfile ? 'Complete Sign Up' : 'Sign Up'
              )}
            </button>
            
            {/* Conditionally render "OR" and Google button only if NOT googleProfile flow */}
            {!googleProfile && (
              <>
                <div className="relative flex items-center py-2">
                  <div className="flex-grow border-t border-gray-300"></div>
                  <span className="flex-shrink mx-3 text-gray-500 text-sm">or</span>
                  <div className="flex-grow border-t border-gray-300"></div>
                </div>
                
                {/* Google Sign In Button */}
                <button 
                  type="button"
                  onClick={handleGoogleSignInClick} // Use the dedicated click handler
                  className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-3" 
                  disabled={isSubmitting}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="text-gray-700 font-medium">Sign in with Google</span>
                </button>
              </>
            )}
          </form>
          
          {/* Conditionally render login link only if NOT googleProfile flow */}
          {!googleProfile && (
            <div className="mt-6 text-center text-sm">
              <span className="text-gray-600">Already have an account? </span>
              <Link to="/" className="text-bela-600 hover:text-bela-700 font-medium">Log in</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignUp;
